/**
 * Manage Images Tool
 *
 * Dialog-driven tool for the project image library. The library data lives
 * in useImageLibraryStore so it persists across tool switches; this tool
 * opens the Manage Images dialog and keeps the legacy link registration.
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { LinkManager } from '../core/LinkManager';
import { useImageLibraryStore, ProjectImage } from '../state/useImageLibraryStore';

export type { ProjectImage };

export class ManageImagesTool extends BaseTool {
    id = 'manage_images';
    name = 'Manage Images';
    cursor = 'default';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    public getAllImages(): ProjectImage[] {
        return useImageLibraryStore.getState().images;
    }

    public getImage(id: string): ProjectImage | undefined {
        return useImageLibraryStore.getState().images.find((i) => i.id === id);
    }

    public addImage(url: string, name?: string): ProjectImage {
        const fileName = url.split(/[\\/]/).pop() || 'Untitled';

        const image = useImageLibraryStore.getState().addImage({
            name: name || fileName,
            url,
            width: 0, // Populated by the actual loader
            height: 0,
            format: 'PNG',
            usedIn: [],
        });

        // Also register as link
        this.linkManager.linkFile('Image', url, { positioning: 'OriginToOrigin' });

        console.log(`Image Added: ${image.name}`);
        return image;
    }

    public removeImage(id: string): boolean {
        return useImageLibraryStore.getState().removeImage(id);
    }

    public reloadImage(id: string): void {
        useImageLibraryStore.getState().reloadImage(id);
    }

    // Tool interface: opens the Manage Images dialog (dialog-driven tool,
    // activation completes immediately; the dialog lives in the UI layer).
    onActivate(): void {
        this.context?.appStore.openManageDialog('images');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void { }
    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }
}
